import axios from "axios";
import { useEffect, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { openRazorpayCheckout } from "../../lib/razorpay";
import { API } from "./shared";

const money = (n) => {
  const v = Number(n || 0);
  return `₹${v.toLocaleString("en-IN", Number.isInteger(v) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// ============================================================
// VIEW: PLANS & BILLING (3-matrix subscription model)
// ============================================================
export function PlansPage({ role, userId }) {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [sub, setSub] = useState(null);
  const [saving, setSaving] = useState("");
  const [payError, setPayError] = useState("");
  const [cycle, setCycle] = useState("monthly");
  const annual = cycle === "annual";

  const load = () => {
    axios.get(`${API}/subscriptions/plans`, { params: { role } }).then(r => setPlans(r.data[role] || []));
    if (userId) axios.get(`${API}/users/${userId}/subscription`).then(r => { setSub(r.data); if ((r.data?.previous_tier ? r.data.previous_cycle : r.data?.billing_cycle) === "annual") setCycle("annual"); }).catch(() => setSub(null));
  };
  useEffect(load, [role, userId]);

  const subscribe = async (tier_id) => {
    if (!userId) return;
    setSaving(tier_id);
    setPayError("");
    try {
      const plan = plans.find(p => p.id === tier_id);
      const priceInr = plan?.billing?.total_inr || 0;
      // Free/downgrade → straight patch (no payment)
      if (priceInr === 0) {
        if (sub?.plan?.billing?.total_inr > 0 && !window.confirm(`Downgrade from ${sub.plan.name} to ${plan?.name || "the free plan"}? You'll lose your paid plan benefits immediately.`)) return;
        await axios.patch(`${API}/users/${userId}/subscription`, { tier_id });
        load();
        return;
      }
      // Paid → Razorpay checkout
      const [cfg, order] = await Promise.all([
        axios.get(`${API}/razorpay/config`).then(r => r.data),
        axios.post(`${API}/razorpay/subscription/order`, null, { params: { tier: tier_id, billing_cycle: cycle } }).then(r => r.data),
      ]);
      // Guard against stale TEST keys in production: refuse to open checkout unless live.
      if (cfg.mode && cfg.mode !== "live" && window.location.hostname.endsWith("goprintpack.com")) {
        setPayError("Payments are temporarily disabled (gateway in test mode). Please contact support.");
        return;
      }
      const resp = await openRazorpayCheckout({
        keyId: cfg.key_id,
        order,
        name: "GoPrintPack",
        description: `${plan?.name || tier_id} subscription (${cycle})`,
        prefill: { name: user?.name || "", email: user?.email || "", contact: user?.phone || "" },
        notes: { tier: tier_id, user_id: userId, billing_cycle: cycle },
      });
      await axios.post(`${API}/razorpay/verify`, {
        ...resp,
        subscription_tier: tier_id,
        user_id: userId,
      });
      load();
    } catch (e) {
      if (e?.code === "USER_CANCELLED") {
        setPayError("Payment cancelled. You can subscribe any time.");
      } else {
        setPayError(e?.description || e?.response?.data?.detail || "Payment failed. Please try again.");
      }
    } finally {
      setSaving("");
    }
  };

  const printerRows = [
      { key: "platform_take_rate_pct", label: "Platform Take-Rate Fee", fmt: v => `${v}% per completed job` },
      { key: "monthly_bid_limit", label: "Monthly Bidding Limit", fmt: v => v == null ? "Unlimited Bids" : `Up to ${v} bids / mo` },
      { key: "escrow_speed_label", label: "Escrow Release Speed" },
      { key: "order_value_cap_label", label: "Order Value Cap" },
      { key: "rfq_notification", label: "RFQ Notification Priority" },
      { key: "capability_tag_label", label: "Capability Tags" },
      { key: "quality_boost", label: "Quality Score Boost" },
  ];
  const rowsForRole = {
    printer: printerRows,
    material_supplier: printerRows,
    finishing_partner: printerRows,
    agency: [
      { key: "design_take_rate_pct", label: "Design Escrow Take-Rate", fmt: v => `${v}% per design job` },
      { key: "directory_placement", label: "Directory Placement" },
      { key: "incoming_rfq_routing", label: "Incoming RFQ Routing" },
      { key: "portfolio_label", label: "Interactive Portfolio" },
      { key: "preflight_integration", label: "Pre-Flight Integration" },
    ],
    buyer: [
      { key: "active_rfq_label", label: "Active RFQ Submissions" },
      { key: "preflight_label", label: "Pre-Flight Artwork Audits" },
      { key: "dedicated_manager", label: "Dedicated Account Manager" },
    ],
  }[role] || [];

  const usage = sub?.usage || {};
  const expiresAt = sub?.expires_at ? new Date(sub.expires_at) : null;
  const daysLeft = expiresAt ? Math.ceil((expiresAt - Date.now()) / 86400000) : null;
  const expiringSoon = daysLeft != null && daysLeft <= 7;
  const prevPlan = sub?.previous_tier ? plans.find(p => p.id === sub.previous_tier) : null;
  const offer = sub?.winback_offer || null;
  const offerMsLeft = offer ? new Date(offer.expires_at) - Date.now() : 0;
  const offerLeft = offerMsLeft > 86400000 ? `${Math.ceil(offerMsLeft / 86400000)} days left` : `${Math.max(1, Math.ceil(offerMsLeft / 3600000))} hours left`;
  const winbackFor = (p) => {
    if (!offer || offer.tier !== p.id) return null;
    const bill = annual ? p.billing.annual : p.billing;
    const base = Math.round(bill.base_inr * (1 - offer.discount_pct / 100) * 100) / 100;
    return { pct: offer.discount_pct, total: Math.round(base * (1 + p.billing.gst_pct / 100) * 100) / 100, was: bill.total_inr };
  };
  const prevWb = prevPlan ? winbackFor(prevPlan) : null;
  const fmtDate = d => d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  return (
    <section className="content" data-testid="plans-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">SUBSCRIPTION PLANS</p>
          <h2>{role === "material_supplier" ? "Material supplier subscription matrix" : role === "finishing_partner" ? "Finishing partner subscription matrix" : role === "printer" ? "Printer / Supplier subscription matrix" : role === "agency" ? "Design & advertising agency matrix" : "Buyer subscription matrix"}</h2>
          <p className="subhead">
            {(role === "material_supplier" || role === "finishing_partner") && "Subscribe to lower the commission on every sub-RFQ you win and get faster escrow payouts."}
            {role === "printer" && "Printers pay a monthly subscription to lower their transaction take-rate, unlock enterprise reverse auctions, and get fast-track escrow payouts."}
            {role === "agency" && "Agencies pay subscriptions to secure client leads, showcase 3D dieline portfolios, and route press-ready artwork directly into active auctions."}
            {role === "buyer" && "Standard buyers submit RFQs for free. High-frequency enterprise buyers (e-commerce, FMCG) subscribe to access volume discounts, dedicated procurement tools, and API access."}
          </p>
        </div>
      </div>

      {sub && (
        <div className="current-plan-card" data-testid="current-plan-card">
          <div>
            <p className="eyebrow">CURRENT PLAN</p>
            <h3>{sub.plan.name} <span className="tier-badge">{sub.plan.tagline}</span></h3>
            <p className="subhead">
              {sub.plan.billing.total_inr > 0
                ? (sub.billing_cycle === "annual"
                  ? `${money(sub.plan.billing.annual.base_inr)}/yr + ${sub.plan.billing.gst_pct}% GST = ${money(sub.plan.billing.annual.total_inr)} billed annually`
                  : `${money(sub.plan.billing.base_inr)}/mo + ${sub.plan.billing.gst_pct}% GST = ${money(sub.plan.billing.total_inr)} billed monthly`)
                : "You're on the free tier. Upgrade any time for higher limits and lower take-rate."}
            </p>
            {expiresAt && (
              <p className="subhead" data-testid="current-plan-expiry">
                {sub.billing_cycle === "annual" ? "Annual" : "Monthly"} plan · {daysLeft > 0 ? `renews/expires on ${fmtDate(expiresAt)} (${daysLeft} day${daysLeft === 1 ? "" : "s"} left)` : `expired on ${fmtDate(expiresAt)}`}
              </p>
            )}
          </div>
          <div className="usage-widgets">
            {usage.monthly_bids && (
              <div className="usage-widget" data-testid="usage-monthly-bids">
                <span>Bids this month</span>
                <b>{usage.monthly_bids.unlimited ? `${usage.monthly_bids.used} · unlimited` : `${usage.monthly_bids.used} / ${usage.monthly_bids.limit}`}</b>
              </div>
            )}
            {usage.active_rfqs && (
              <div className="usage-widget" data-testid="usage-active-rfqs">
                <span>Open RFQs</span>
                <b>{usage.active_rfqs.unlimited ? `${usage.active_rfqs.used} · unlimited` : `${usage.active_rfqs.used} / ${usage.active_rfqs.limit}`}</b>
              </div>
            )}
            {usage.portfolio_samples && (
              <div className="usage-widget" data-testid="usage-portfolio">
                <span>Portfolio samples</span>
                <b>{usage.portfolio_samples.unlimited ? `${usage.portfolio_samples.used} · unlimited` : `${usage.portfolio_samples.used} / ${usage.portfolio_samples.limit}`}</b>
              </div>
            )}
          </div>
        </div>
      )}

      {sub && expiringSoon && (
        <div className="renewal-banner" data-testid="renewal-banner">
          <span>{daysLeft > 0 ? `Your ${sub.plan.name} plan expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}. Renew now so your benefits don't lapse.` : `Your ${sub.plan.name} plan expired on ${fmtDate(expiresAt)}. Benefits stay active until ${fmtDate(new Date(expiresAt.getTime() + (sub.grace_days || 3) * 86400000))}, then your account moves to the free plan. Renew now.`}</span>
          <button className="primary-btn" data-testid="renewal-banner-renew-btn" disabled={saving === sub.tier_id} onClick={() => subscribe(sub.tier_id)}>
            {saving === sub.tier_id ? "Processing…" : `Renew ${annual ? "annually" : "monthly"}`}
          </button>
        </div>
      )}

      {sub && sub.previous_tier && prevPlan && (
        <div className="renewal-banner" data-testid="lapsed-plan-banner">
          <span>
            Your {prevPlan.name} plan expired{sub.lapsed_at ? ` on ${fmtDate(new Date(sub.lapsed_at))}` : ""} and your account moved to {sub.plan.name}.{" "}
            {prevWb ? (
              <b data-testid="winback-offer-text">Comeback offer: {prevWb.pct}% off your first {annual ? "annual" : "monthly"} payment · {offerLeft} (until {fmtDate(new Date(offer.expires_at))})</b>
            ) : "Renew to restore your benefits."}
          </span>
          <button className="primary-btn" data-testid="lapsed-plan-renew-btn" disabled={saving === prevPlan.id} onClick={() => subscribe(prevPlan.id)}>
            {saving === prevPlan.id ? "Processing…" : `${prevWb ? `Claim ${prevWb.pct}% off` : `Renew ${prevPlan.name}`} · ${money(prevWb ? prevWb.total : (annual ? prevPlan.billing.annual.total_inr : prevPlan.billing.total_inr))}${annual ? "/yr" : "/mo"}`}
          </button>
        </div>
      )}

      <div className="billing-toggle" role="tablist" data-testid="billing-cycle-toggle">
        <button className={!annual ? "active" : ""} data-testid="billing-cycle-monthly" onClick={() => setCycle("monthly")}>Monthly</button>
        <button className={annual ? "active" : ""} data-testid="billing-cycle-annual" onClick={() => setCycle("annual")}>
          Pay Annually<span className="save-chip">Save 20%</span>
        </button>
      </div>

      {!plans.length && <p className="subhead" data-testid="plans-loading">Loading plans…</p>}
      <div className={`plan-matrix cols-${plans.length}`} data-testid="plan-matrix">
        <div className="plan-col plan-labels">
          <div className="plan-header-empty"/>
          <div className="plan-price-empty"/>
          {rowsForRole.map(r => <div key={r.key} className="plan-row-label">{r.label}</div>)}
          <div className="plan-row-label"/>
        </div>
        {plans.map(p => {
          const isCurrentTier = sub?.tier_id === p.id;
          const isPaid = p.billing.total_inr > 0;
          const isCurrent = isCurrentTier && (!isPaid || ((sub?.billing_cycle || "monthly") === cycle && !expiringSoon));
          const a = p.billing.annual;
          const wb = winbackFor(p);
          return (
            <div key={p.id} className={`plan-col ${isCurrent ? "current" : ""} ${p.tagline === "Most popular" ? "highlight" : ""}`} data-testid={`plan-col-${p.id}`}>
              <div className="plan-header">
                <b>{p.name}</b>
                <small>{p.tagline}</small>
              </div>
              <div className="plan-price">
                {wb && <div className="price-save" data-testid={`plan-${p.id}-winback`}>Comeback offer · {wb.pct}% off first payment = {money(wb.total)}{annual ? "/yr" : "/mo"}</div>}
                {isPaid && annual ? (
                  <>
                    <span className="price-strike" data-testid={`plan-${p.id}-monthly-strike`}>{money(p.billing.base_inr)}</span>
                    <span className="price-main" data-testid={`plan-${p.id}-price`}>{money(a.monthly_equiv_inr)}</span>
                    <span className="price-per">/mo</span>
                    <div className="price-gst">Billed {money(a.base_inr)}/yr + 18% GST = {money(a.total_inr)}/yr</div>
                    <div className="price-save" data-testid={`plan-${p.id}-savings`}>You save {money(a.savings_inr)}/yr</div>
                  </>
                ) : isPaid ? (
                  <>
                    <span className="price-main" data-testid={`plan-${p.id}-price`}>{money(p.billing.base_inr)}</span>
                    <span className="price-per">/mo</span>
                    <div className="price-gst">+ 18% GST = {money(p.billing.total_inr)}/mo</div>
                  </>
                ) : (
                  <span className="price-main">Free</span>
                )}
              </div>
              {rowsForRole.map(r => (
                <div key={r.key} className="plan-cell">
                  {r.fmt ? r.fmt(p[r.key]) : p[r.key]}
                </div>
              ))}
              <div className="plan-cell action">
                {isCurrent ? (
                  <span className="current-pill"><CheckCircle2 size={13}/> Current plan</span>
                ) : (
                  <button
                    data-testid={`subscribe-${p.id}-btn`}
                    className={!isPaid && sub?.plan?.billing?.total_inr > 0 ? "secondary-btn full" : "primary-btn full"}
                    disabled={saving === p.id}
                    onClick={() => subscribe(p.id)}
                  >
                    {saving === p.id ? "Subscribing…" : !isPaid ? "Switch to Free"
                      : `${isCurrentTier ? (expiringSoon && (sub?.billing_cycle || "monthly") === cycle ? "Renew" : `Switch to ${cycle}`) : wb ? `Claim ${wb.pct}% off` : "Subscribe"} · ${money(wb ? wb.total : (annual ? a.total_inr : p.billing.total_inr))}${annual ? "/yr" : "/mo"}`}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="plan-fineprint">All prices in Indian Rupees (₹). 18% GST applied at checkout. Annual plans are billed upfront for 12 months at 20% off. Renewing early adds the new period to your remaining days. Paid tiers are billed via Razorpay Standard Checkout — cards, UPI, netbanking, wallets and EMI supported.</p>
      {payError && (
        <div className="banner-error" data-testid="plans-pay-error" style={{ marginTop: 16 }}>
          {payError}
        </div>
      )}
    </section>
  );
}
